/**
 * The signed-in person's profile and display preferences — `GET /api/me/profile`.
 *
 * One request per sign-in, shared: the top bar (job title, Settings), App
 * (whether GRiPS shows) and the default landing all read it. A module-level
 * cache keyed on the token makes them share one round trip, and a saved
 * preference is pushed to every subscriber at once, so hiding GRiPS in
 * Settings hides it immediately.
 *
 * Display only, so it fails soft: a failed load reads as the defaults.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

export type LandingPreference = 'home' | 'approvals';

export interface MyPreferences {
  /** null = the default (approvers land on the desk, everyone else on Home). */
  landingPage: LandingPreference | null;
  showGrips: boolean;
}

export interface MyProfile {
  /** The person's job title (their HR position's title), shown under their name. */
  jobTitle: string | null;
  preferences: MyPreferences;
  /** The read failed: these are defaults, not what the person saved. */
  failed?: boolean;
}

export const DEFAULT_PREFERENCES: MyPreferences = { landingPage: null, showGrips: true };
const DEFAULT_PROFILE: MyProfile = {
  jobTitle: null,
  preferences: DEFAULT_PREFERENCES,
};

function shapePreferences(p: any): MyPreferences {
  return {
    landingPage: p?.landingPage === 'home' || p?.landingPage === 'approvals' ? p.landingPage : null,
    showGrips: p?.showGrips !== false,
  };
}

let cache: { token: string; promise: Promise<MyProfile>; value?: MyProfile } | null = null;
const listeners = new Set<(p: MyProfile) => void>();

function publish(profile: MyProfile): void {
  if (cache) cache.value = profile;
  listeners.forEach(l => l(profile));
}

export function fetchMyProfile(token: string): Promise<MyProfile> {
  if (cache?.token === token) return cache.promise;
  const entry: NonNullable<typeof cache> = {
    token,
    promise: axios
      .get('/api/me/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const d = r?.data?.data;
        const jobTitle = typeof d?.jobTitle === 'string' ? d.jobTitle.trim() : '';
        return {
          jobTitle: jobTitle || null,
          preferences: shapePreferences(d?.preferences),
        };
      })
      .catch((): MyProfile => ({ ...DEFAULT_PROFILE, failed: true }))
      .then(profile => {
        entry.value = profile;
        return profile;
      }),
  };
  cache = entry;
  return entry.promise;
}

/** Forget the cached profile. For tests, and for a sign-out that does not reload. */
export function resetMyProfileCache(): void {
  cache = null;
}

export interface UseMyProfile {
  profile: MyProfile;
  /** True until the profile for this token has resolved (or failed). */
  loading: boolean;
  /** Save a change; resolves with the stored preferences, rejects on failure. */
  savePreferences: (patch: Partial<MyPreferences>) => Promise<MyPreferences>;
}

export function useMyProfile(token: string | null | undefined): UseMyProfile {
  const cached = token && cache?.token === token ? cache.value : undefined;
  const [profile, setProfile] = useState<MyProfile | undefined>(cached);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    const onChange = (p: MyProfile) => live && setProfile(p);
    listeners.add(onChange);
    fetchMyProfile(token).then(p => live && setProfile(p));
    return () => {
      live = false;
      listeners.delete(onChange);
    };
  }, [token]);

  const savePreferences = useCallback(
    async (patch: Partial<MyPreferences>): Promise<MyPreferences> => {
      const r = await axios.patch('/api/me/preferences', patch, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const preferences = shapePreferences(r?.data?.data);
      const base = (token && cache?.token === token && cache.value) || profile || DEFAULT_PROFILE;
      publish({ ...base, preferences });
      return preferences;
    },
    [token, profile]
  );

  const current = profile ?? cached;
  return { profile: current ?? DEFAULT_PROFILE, loading: !!token && !current, savePreferences };
}
